import React, { useEffect, useState } from 'react';
import { ShoppingCart, X, Minus, Plus, Clock, AlertCircle, Info, Tag, CheckCircle, Package, MapPin, Calendar, Wallet, Timer } from 'lucide-react';
import { Card } from '../components/ui/Card';
import { Button } from '../components/ui/Button';
import { SearchBar } from '../components/ui/SearchBar';
import { useToast } from '../components/ui/Toast';
import { useOrders } from '../contexts/OrderContext';
import { useAuth } from '../contexts/AuthContext';
import { getSettings } from '../utils/settings';
import { getCompanies } from '../utils/companyManagement';
import { supabaseClient, isSupabaseConfigured } from '../lib/supabase';
import { useNavigate } from 'react-router-dom';
import { MenuItemOptionsModal } from '../components/MenuItemOptionsModal'; // Import modal

export function EmployeeOrderPage() {
  const [menuItems, setMenuItems] = useState<any[]>([]);
  const [categories, setCategories] = useState<string[]>([]);
  const [loading, setLoading] = useState(true);
  const [cart, setCart] = useState<{
    [key: string]: number;
  }>({});
  const [selectedCategory, setSelectedCategory] = useState('Todos');
  const [searchTerm, setSearchTerm] = useState('');
  const [showCheckout, setShowCheckout] = useState(false);
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [confirmedOrder, setConfirmedOrder] = useState<any>(null);
  const [timeRemaining, setTimeRemaining] = useState('');
  const [canOrder, setCanOrder] = useState(true);
  const [selectedItemForOptions, setSelectedItemForOptions] = useState<any>(null); // New state
  const [isProcessingOrder, setIsProcessingOrder] = useState(false); // New loading state

  const {
    addOrder
  } = useOrders();
  const {
    user
  } = useAuth();
  const {
    showToast
  } = useToast();
  const navigate = useNavigate();

  // Load menu items from Supabase/localStorage
  useEffect(() => {
    loadMenuData();
  }, []);
  const loadMenuData = async () => {
    setLoading(true);
    try {
      if (isSupabaseConfigured()) {
        // Load from Supabase
        const [items, cats] = await Promise.all([supabaseClient.getMenuItems(), supabaseClient.getMenuCategories()]);
        if (items.length > 0) {
          setMenuItems(items);
          console.log('✅ Loaded', items.length, 'menu items from Supabase');
        } else {
          // Fallback to localStorage
          const savedItems = localStorage.getItem('criket_menu_items');
          setMenuItems(savedItems ? JSON.parse(savedItems) : []);
        }
        if (cats.length > 0) {
          setCategories(cats);
        } else {
          const savedCats = localStorage.getItem('criket_menu_categories');
          setCategories(savedCats ? JSON.parse(savedCats) : ['Platos Principales', 'Entradas', 'Ensaladas', 'Pastas', 'Sandwiches']);
        }
      } else {
        // Fallback to localStorage
        const savedItems = localStorage.getItem('criket_menu_items');
        const savedCats = localStorage.getItem('criket_menu_categories');
        setMenuItems(savedItems ? JSON.parse(savedItems) : []);
        setCategories(savedCats ? JSON.parse(savedCats) : ['Platos Principales', 'Entradas', 'Ensaladas', 'Pastas', 'Sandwiches']);
      }
    } catch (error) {
      console.error('Error loading menu data:', error);
      // Fallback to localStorage
      const savedItems = localStorage.getItem('criket_menu_items');
      const savedCats = localStorage.getItem('criket_menu_categories');
      setMenuItems(savedItems ? JSON.parse(savedItems) : []);
      setCategories(savedCats ? JSON.parse(savedCats) : ['Platos Principales', 'Entradas', 'Ensaladas', 'Pastas', 'Sandwiches']);
    } finally {
      setLoading(false);
    }
  };

  // Check if user's company is tax exempt and get subsidy
  const companies = getCompanies();
  const userCompany = companies.find(c => c.name === user?.company);
  const isTaxExempt = userCompany?.taxExempt || false;
  const companySubsidy = userCompany?.subsidy || '0';
  const TAX_RATE = 0.18;

  // Calculate subsidy amount
  const calculateSubsidyAmount = (subtotal: number): number => {
    if (!companySubsidy || companySubsidy === '0' || companySubsidy === 'RD$ 0') {
      return 0;
    }
    if (companySubsidy.includes('%')) {
      const percentage = parseFloat(companySubsidy.replace('%', ''));
      return subtotal * (percentage / 100);
    }
    if (companySubsidy.includes('RD$')) {
      return parseFloat(companySubsidy.replace('RD$', '').trim());
    }
    return 0;
  };

  // Check ordering hours
  useEffect(() => {
    const checkOrderingTime = () => {
      const settings = getSettings();
      const now = new Date();
      const [cutoffHour, cutoffMinute] = settings.orderCutoffTime.split(':').map(Number);
      const cutoffTime = new Date();
      cutoffTime.setHours(cutoffHour, cutoffMinute, 0, 0);
      const timeDiff = cutoffTime.getTime() - now.getTime();
      if (timeDiff <= 0) {
        setCanOrder(false);
        setTimeRemaining('00h 00m 00s');
      } else {
        setCanOrder(true);
        const hours = Math.floor(timeDiff / (1000 * 60 * 60));
        const minutes = Math.floor(timeDiff % (1000 * 60 * 60) / (1000 * 60));
        const seconds = Math.floor(timeDiff % (1000 * 60) / 1000);
        setTimeRemaining(`${hours}h ${minutes}m ${seconds.toString().padStart(2, '0')}s`);
      }
    };
    checkOrderingTime();
    const interval = setInterval(checkOrderingTime, 1000);
    return () => clearInterval(interval);
  }, []);
  const filteredItems = menuItems.filter(item => {
    const matchesCategory = selectedCategory === 'Todos' || item.category === selectedCategory;
    const matchesSearch = item.name.toLowerCase().includes(searchTerm.toLowerCase());
    return matchesCategory && matchesSearch;
  });
  const handleAddToCartClick = (item: any) => {
    if (!canOrder) return;
    // Open options modal instead of adding directly
    setSelectedItemForOptions(item);
  };
  const handleConfirmOptions = (selectedOptions: any[], totalPrice: number) => {
    if (!selectedItemForOptions) return;
    const cartItemId = `${selectedItemForOptions.id}-${Date.now()}`; // Unique ID for cart item with options

    // Create a custom item with options
    const customItem = {
      ...selectedItemForOptions,
      id: cartItemId,
      originalId: selectedItemForOptions.id,
      price: totalPrice,
      selectedOptions: selectedOptions
    };

    // Add to cart logic (modified for custom items)
    setCart(prev => ({
      ...prev,
      [cartItemId]: 1
    }));

    // We need to store the custom item details somewhere since they aren't in menuItems
    // For simplicity, we'll append to menuItems state temporarily
    setMenuItems(prev => [...prev, customItem]);
    setSelectedItemForOptions(null);
    showToast('success', 'Agregado', 'Plato agregado al carrito');
  };
  const addToCart = (itemId: string) => {
    if (!canOrder) return;
    setCart(prev => ({
      ...prev,
      [itemId]: (prev[itemId] || 0) + 1
    }));
  };
  const removeFromCart = (itemId: string) => {
    setCart(prev => {
      const newCart = {
        ...prev
      };
      if (newCart[itemId] > 1) {
        newCart[itemId]--;
      } else {
        delete newCart[itemId];
      }
      return newCart;
    });
  };
  const cartItems = Object.entries(cart).map(([itemId, quantity]) => {
    const item = menuItems.find(i => i.id === itemId);
    return {
      item,
      quantity
    };
  }).filter(x => x.item);

  // Calculate totals
  const subtotal = cartItems.reduce((total, {
    item,
    quantity
  }) => {
    return total + (item?.price || 0) * quantity;
  }, 0);
  const tax = isTaxExempt ? 0 : subtotal * TAX_RATE;
  const subsidyAmount = calculateSubsidyAmount(subtotal);
  const totalBeforeSubsidy = subtotal + tax;
  const total = totalBeforeSubsidy - subsidyAmount;
  const handleCheckout = () => {
    if (!user || !canOrder) {
      console.error('❌ Cannot checkout:', {
        user,
        canOrder
      });
      return;
    }

    // Changed: Remove quantity prefix from product names
    const orderItems = cartItems.map(({
      item,
      quantity
    }) => item?.name).join(', ');
    const orderData = {
      deliveryDate: new Date().toLocaleString('es-DO'),
      type: 'Envío',
      name: user.name,
      company: user.company || 'Sin empresa',
      branch: user.branch || 'Sin sucursal',
      product: orderItems,
      cashier: 'Online',
      quantity: cartItems.reduce((sum, {
        quantity
      }) => sum + quantity, 0),
      catalogType: 'Almuerzo',
      subtotal: subtotal.toFixed(2),
      discount: '0',
      total: total.toFixed(2),
      taxExempt: isTaxExempt,
      employeeId: user.id
    };
    try {
      // Show loading state
      setIsProcessingOrder(true);
      setShowCheckout(false);
      addOrder(orderData);

      // Store order details for confirmation page
      const orderDetails = {
        ...orderData,
        items: cartItems,
        subsidyAmount: subsidyAmount.toFixed(2),
        tax: tax.toFixed(2)
      };

      // Wait 3 seconds before showing confirmation
      setTimeout(() => {
        setConfirmedOrder(orderDetails);
        setCart({});
        setIsProcessingOrder(false);
        setShowConfirmation(true);
      }, 3000);
    } catch (error) {
      console.error('❌ Error adding order:', error);
      setIsProcessingOrder(false);
      showToast('error', 'Error al crear pedido', 'Por favor intenta nuevamente');
    }
  };
  const settings = getSettings();
  return <div className="min-h-screen bg-gray-50 dark:bg-gray-950" data-id="element-1226">
      {/* Header Section */}
      <div className="bg-white dark:bg-gray-900 border-b border-gray-200 dark:border-gray-800" data-id="element-1227">
        <div className="max-w-7xl mx-auto px-4 lg:px-8 py-6" data-id="element-1228">
          {/* Title */}
          <div className="mb-6" data-id="element-1229">
            <h1 className="text-3xl font-bold text-gray-900 dark:text-white mb-2" data-id="element-1230">
              Realizar Pedido
            </h1>
            <p className="text-gray-600 dark:text-gray-400" data-id="element-1231">
              Selecciona tu almuerzo para hoy
            </p>
          </div>

          {/* Info Banners - MEJORADO */}
          <div className="flex flex-col md:flex-row gap-4" data-id="element-1232">
            {/* Timer Card Improved */}
            <div className={`flex-1 relative overflow-hidden rounded-2xl border transition-all duration-300 ${canOrder ? 'bg-gradient-to-br from-emerald-50 to-teal-50 dark:from-emerald-900/20 dark:to-teal-900/20 border-emerald-200 dark:border-emerald-800' : 'bg-gradient-to-br from-red-50 to-orange-50 dark:from-red-900/20 dark:to-orange-900/20 border-red-200 dark:border-red-800'} p-4 shadow-sm hover:shadow-md`} data-id="element-1233">
              <div className="flex items-center justify-between" data-id="element-1234">
                <div className="flex items-center gap-4" data-id="element-1235">
                  <div className={`p-3 rounded-xl ${canOrder ? 'bg-emerald-100 text-emerald-600 dark:bg-emerald-900/40 dark:text-emerald-400' : 'bg-red-100 text-red-600 dark:bg-red-900/40 dark:text-red-400'}`} data-id="element-1236">
                    {canOrder ? <Timer size={24} data-id="element-1237" /> : <AlertCircle size={24} data-id="element-1238" />}
                  </div>
                  <div data-id="element-1239">
                    <p className={`text-xs font-bold uppercase tracking-wider mb-0.5 ${canOrder ? 'text-emerald-600 dark:text-emerald-400' : 'text-red-600 dark:text-red-400'}`} data-id="element-1240">
                      {canOrder ? 'Tiempo Restante' : 'Pedidos Cerrados'}
                    </p>
                    <div className={`text-2xl font-mono font-bold leading-none ${canOrder ? 'text-emerald-900 dark:text-emerald-100' : 'text-red-900 dark:text-red-100'}`} data-id="element-1241">
                      {canOrder ? timeRemaining : 'Cerrado'}
                    </div>
                  </div>
                </div>

                <div className="text-right hidden sm:block border-l border-gray-200 dark:border-gray-700 pl-4 ml-4" data-id="element-1242">
                  <p className="text-xs text-gray-500 dark:text-gray-400 font-medium" data-id="element-1243">
                    Hora de Cierre
                  </p>
                  <p className="text-lg font-bold text-gray-900 dark:text-white" data-id="element-1244">
                    {settings.orderCutoffTime}
                  </p>
                </div>
              </div>
            </div>

            {/* Subsidy Info Improved */}
            {(isTaxExempt || companySubsidy && companySubsidy !== '0' && companySubsidy !== 'RD$ 0') && <div className="flex-1 relative overflow-hidden rounded-2xl border border-blue-200 dark:border-blue-800 bg-gradient-to-br from-blue-50 to-indigo-50 dark:from-blue-900/20 dark:to-indigo-900/20 p-4 shadow-sm hover:shadow-md transition-all" data-id="element-1245">
                <div className="flex items-center gap-4 h-full" data-id="element-1246">
                  <div className="p-3 rounded-xl bg-blue-100 text-blue-600 dark:bg-blue-900/40 dark:text-blue-400" data-id="element-1247">
                    <Wallet size={24} data-id="element-1248" />
                  </div>
                  <div className="flex-1" data-id="element-1249">
                    <p className="text-xs font-bold uppercase tracking-wider text-blue-600 dark:text-blue-400 mb-2" data-id="element-1250">
                      Tus Beneficios
                    </p>
                    <div className="flex flex-wrap gap-2" data-id="element-1251">
                      {isTaxExempt && <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-lg bg-white dark:bg-blue-950 border border-blue-200 dark:border-blue-800 text-sm font-semibold text-blue-700 dark:text-blue-300 shadow-sm" data-id="element-1252">
                          <CheckCircle size={14} data-id="element-1253" />
                          Exento ITBIS
                        </span>}
                      {companySubsidy && companySubsidy !== '0' && companySubsidy !== 'RD$ 0' && <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-lg bg-indigo-50 dark:bg-indigo-950 border border-indigo-200 dark:border-indigo-800 text-sm font-semibold text-indigo-700 dark:text-indigo-300 shadow-sm" data-id="element-1254">
                            <Tag size={14} data-id="element-1255" />
                            Subsidio: {companySubsidy}
                          </span>}
                    </div>
                  </div>
                </div>
              </div>}
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-4 lg:px-8 py-8" data-id="element-1256">
        {/* Filters Section */}
        <div className="mb-8" data-id="element-1257">
          <div className="flex flex-col md:flex-row gap-4 items-start md:items-center justify-between" data-id="element-1258">
            {/* Category Pills */}
            <div className="flex gap-2 overflow-x-auto pb-2 md:pb-0 scrollbar-hide" data-id="element-1259">
              <button onClick={() => setSelectedCategory('Todos')} disabled={!canOrder} className={`px-5 py-2.5 rounded-full text-sm font-semibold whitespace-nowrap transition-all ${selectedCategory === 'Todos' ? 'bg-black dark:bg-white text-white dark:text-black shadow-md' : 'bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 text-gray-700 dark:text-gray-300 hover:border-gray-300 dark:hover:border-gray-600'} ${!canOrder ? 'opacity-50 cursor-not-allowed' : ''}`} data-id="element-1260">
                Todos
              </button>
              {categories.map(cat => <button key={cat} onClick={() => setSelectedCategory(cat)} disabled={!canOrder} className={`px-5 py-2.5 rounded-full text-sm font-semibold whitespace-nowrap transition-all ${selectedCategory === cat ? 'bg-black dark:bg-white text-white dark:text-black shadow-md' : 'bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 text-gray-700 dark:text-gray-300 hover:border-gray-300 dark:hover:border-gray-600'} ${!canOrder ? 'opacity-50 cursor-not-allowed' : ''}`} data-id="element-1261">
                  {cat}
                </button>)}
            </div>

            {/* Search */}
            <SearchBar placeholder="Buscar plato..." className="w-full md:w-80" onSearch={setSearchTerm} data-id="element-1262" />
          </div>
        </div>

        {/* Menu Grid */}
        {loading ? <div className="flex items-center justify-center py-20" data-id="element-1263">
            <div className="text-center" data-id="element-1264">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-black dark:border-white mx-auto mb-4" data-id="element-1265"></div>
              <p className="text-gray-500 dark:text-gray-400" data-id="element-1266">
                Cargando menú...
              </p>
            </div>
          </div> : filteredItems.length === 0 ? <div className="text-center py-20 bg-white dark:bg-gray-900 rounded-2xl border border-gray-200 dark:border-gray-800" data-id="element-1267">
            <Package size={48} className="mx-auto text-gray-300 dark:text-gray-600 mb-4" data-id="element-1268" />
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2" data-id="element-1269">
              No hay platos disponibles
            </h3>
            <p className="text-gray-500 dark:text-gray-400" data-id="element-1270">
              {searchTerm ? 'Intenta con otra búsqueda' : 'El menú aún no está disponible'}
            </p>
          </div> : <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" data-id="element-1271">
            {filteredItems.filter(i => !i.originalId).map(item => <div key={item.id} className={`group bg-white dark:bg-gray-900 rounded-2xl border border-gray-200 dark:border-gray-800 overflow-hidden transition-all hover:shadow-xl hover:border-gray-300 dark:hover:border-gray-700 ${!canOrder ? 'opacity-60' : ''}`} data-id="element-1272">
                  {/* Image */}
                  <div className="relative h-56 bg-gray-100 dark:bg-gray-800 overflow-hidden" data-id="element-1273">
                    <img src={item.image} alt={item.name} className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110" data-id="element-1274" />
                    {!canOrder && <div className="absolute inset-0 bg-black/60 flex items-center justify-center backdrop-blur-sm" data-id="element-1275">
                        <p className="text-white font-bold text-center px-4" data-id="element-1276">
                          Fuera de horario
                        </p>
                      </div>}
                  </div>

                  {/* Content */}
                  <div className="p-6" data-id="element-1277">
                    {/* Title & Description */}
                    <div className="mb-4" data-id="element-1278">
                      <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-2 line-clamp-1" data-id="element-1279">
                        {item.name}
                      </h3>
                      <p className="text-sm text-gray-600 dark:text-gray-400 line-clamp-2 leading-relaxed" data-id="element-1280">
                        {item.description}
                      </p>
                    </div>

                    {/* Price & Button */}
                    <div className="flex items-center justify-between pt-4 border-t border-gray-100 dark:border-gray-800" data-id="element-1281">
                      <div data-id="element-1282">
                        <p className="text-2xl font-bold text-gray-900 dark:text-white" data-id="element-1283">
                          RD$ {item.price.toFixed(2)}
                        </p>
                        {!isTaxExempt && <p className="text-xs text-gray-500 dark:text-gray-400" data-id="element-1284">
                            +ITBIS
                          </p>}
                      </div>

                      <Button size="sm" onClick={() => handleAddToCartClick(item)} disabled={!canOrder} className="px-6" data-id="element-1285">
                        Agregar
                      </Button>
                    </div>
                  </div>
                </div>)}
          </div>}
      </div>

      {/* Floating Cart Button */}
      {Object.keys(cart).length > 0 && canOrder && <button onClick={() => setShowCheckout(true)} className="fixed bottom-8 right-8 z-50 bg-black dark:bg-white text-white dark:text-black p-5 rounded-full shadow-2xl hover:scale-110 transition-transform" data-id="element-1286">
          <div className="relative" data-id="element-1287">
            <ShoppingCart size={24} data-id="element-1288" />
            <span className="absolute -top-2 -right-2 bg-white dark:bg-black text-black dark:text-white text-xs font-bold w-6 h-6 rounded-full flex items-center justify-center border-2 border-black dark:border-white" data-id="element-1289">
              {Object.values(cart).reduce((a, b) => a + b, 0)}
            </span>
          </div>
        </button>}

      {/* Checkout Modal */}
      {showCheckout && <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4" data-id="element-1290">
          <div className="bg-white dark:bg-gray-900 rounded-xl shadow-2xl w-full max-w-md border border-gray-200 dark:border-gray-800 max-h-[90vh] overflow-y-auto" data-id="element-1291">
            <div className="flex items-center justify-between p-6 border-b border-gray-200 dark:border-gray-800 sticky top-0 bg-white dark:bg-gray-900 z-10" data-id="element-1292">
              <h2 className="text-xl font-bold text-gray-900 dark:text-white" data-id="element-1293">
                Confirmar Pedido
              </h2>
              <button onClick={() => setShowCheckout(false)} className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300" data-id="element-1294">
                <X size={20} data-id="element-1295" />
              </button>
            </div>

            <div className="p-6 space-y-4" data-id="element-1296">
              {cartItems.map(({
            item,
            quantity
          }) => item && <div key={item.id} className="flex flex-col gap-2 p-4 bg-gray-50 dark:bg-gray-800 rounded-lg" data-id="element-1297">
                      <div className="flex items-center gap-4" data-id="element-1298">
                        <img src={item.image} alt={item.name} className="w-16 h-16 rounded-lg object-cover" data-id="element-1299" />
                        <div className="flex-1" data-id="element-1300">
                          <h3 className="font-bold text-gray-900 dark:text-white" data-id="element-1301">
                            {item.name}
                          </h3>
                          <div className="flex items-center gap-2 mt-1" data-id="element-1302">
                            <button onClick={() => removeFromCart(item.id)} className="w-6 h-6 rounded-full bg-gray-200 dark:bg-gray-700 flex items-center justify-center" data-id="element-1303">
                              <Minus size={12} data-id="element-1304" />
                            </button>
                            <span className="text-sm font-medium" data-id="element-1305">
                              {quantity}
                            </span>
                            <button onClick={() => addToCart(item.id)} className="w-6 h-6 rounded-full bg-gray-200 dark:bg-gray-700 flex items-center justify-center" data-id="element-1306">
                              <Plus size={12} data-id="element-1307" />
                            </button>
                          </div>
                        </div>
                        <p className="font-bold text-gray-900 dark:text-white" data-id="element-1308">
                          RD$ {(item.price * quantity).toFixed(2)}
                        </p>
                      </div>

                      {/* Show selected options */}
                      {item.selectedOptions && item.selectedOptions.length > 0 && <div className="text-xs text-gray-500 dark:text-gray-400 pl-20 border-l-2 border-gray-200 dark:border-gray-700 ml-2" data-id="element-1309">
                            {item.selectedOptions.map((opt: any, idx: number) => <div key={idx} data-id="element-1310">
                                  • {opt.optionName}{' '}
                                  {opt.priceModifier > 0 && `(+RD$ ${opt.priceModifier})`}
                                </div>)}
                          </div>}
                    </div>)}

              <div className="border-t border-gray-200 dark:border-gray-800 pt-4 space-y-2" data-id="element-1311">
                <div className="flex justify-between text-sm" data-id="element-1312">
                  <span className="text-gray-500 dark:text-gray-400" data-id="element-1313">
                    Subtotal
                  </span>
                  <span className="font-medium text-gray-900 dark:text-white" data-id="element-1314">
                    RD$ {subtotal.toFixed(2)}
                  </span>
                </div>

                <div className="flex justify-between text-sm" data-id="element-1315">
                  <span className="text-gray-500 dark:text-gray-400" data-id="element-1316">
                    ITBIS (18%)
                    {isTaxExempt && <span className="ml-2 text-xs text-blue-600 dark:text-blue-400" data-id="element-1317">
                        (Exento)
                      </span>}
                  </span>
                  <span className="font-medium text-gray-900 dark:text-white" data-id="element-1318">
                    RD$ {tax.toFixed(2)}
                  </span>
                </div>

                {subsidyAmount > 0 && <div className="flex justify-between text-sm" data-id="element-1319">
                    <span className="text-green-600 dark:text-green-400 flex items-center gap-1" data-id="element-1320">
                      <Tag size={14} data-id="element-1321" />
                      Subsidio {companySubsidy}
                    </span>
                    <span className="font-medium text-green-600 dark:text-green-400" data-id="element-1322">
                      - RD$ {subsidyAmount.toFixed(2)}
                    </span>
                  </div>}

                <div className="flex justify-between text-lg font-bold pt-2 border-t border-gray-200 dark:border-gray-800" data-id="element-1323">
                  <span className="text-gray-900 dark:text-white" data-id="element-1324">
                    Total a Pagar
                  </span>
                  <span className="text-gray-900 dark:text-white" data-id="element-1325">
                    RD$ {total.toFixed(2)}
                  </span>
                </div>

                {subsidyAmount > 0 && <div className="bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg p-3" data-id="element-1326">
                    <div className="flex items-start gap-2" data-id="element-1327">
                      <Tag className="text-green-600 dark:text-green-400 shrink-0 mt-0.5" size={16} data-id="element-1328" />
                      <div className="text-xs text-green-900 dark:text-green-100" data-id="element-1329">
                        <p className="font-semibold mb-1" data-id="element-1330">
                          ¡Subsidio Aplicado!
                        </p>
                        <p className="text-green-700 dark:text-green-300" data-id="element-1331">
                          Tu empresa subsidia {companySubsidy} de tu pedido.
                          Ahorras RD$ {subsidyAmount.toFixed(2)}
                        </p>
                      </div>
                    </div>
                  </div>}
              </div>

              <div className="space-y-3 pt-4" data-id="element-1332">
                <div data-id="element-1333">
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1" data-id="element-1334">
                    Tipo de Entrega
                  </label>
                  <select className="w-full px-3 py-2 border border-gray-300 dark:border-gray-700 rounded-lg focus:outline-none focus:ring-1 focus:ring-black dark:focus:ring-white bg-white dark:bg-gray-800 text-gray-900 dark:text-white" data-id="element-1335">
                    <option data-id="element-1336">Envío a sucursal</option>
                    <option data-id="element-1337">Retiro en punto</option>
                  </select>
                </div>

                <div data-id="element-1338">
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1" data-id="element-1339">
                    Notas especiales (opcional)
                  </label>
                  <textarea className="w-full px-3 py-2 border border-gray-300 dark:border-gray-700 rounded-lg focus:outline-none focus:ring-1 focus:ring-black dark:focus:ring-white bg-white dark:bg-gray-800 text-gray-900 dark:text-white" rows={3} placeholder="Ej: Sin cebolla, extra salsa..." data-id="element-1340" />
                </div>
              </div>
            </div>

            <div className="p-6 border-t border-gray-200 dark:border-gray-800 flex gap-3 sticky bottom-0 bg-white dark:bg-gray-900" data-id="element-1341">
              <Button variant="outline" onClick={() => setShowCheckout(false)} fullWidth data-id="element-1342">
                Cancelar
              </Button>
              <Button onClick={handleCheckout} fullWidth data-id="element-1343">
                Confirmar Pedido
              </Button>
            </div>
          </div>
        </div>}

      {/* Options Modal */}
      <MenuItemOptionsModal menuItem={selectedItemForOptions} isOpen={!!selectedItemForOptions} onClose={() => setSelectedItemForOptions(null)} onConfirm={handleConfirmOptions} data-id="element-1344" />

      {/* Processing Order Loading Modal */}
      {isProcessingOrder && <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4" data-id="element-1345">
          <div className="bg-white dark:bg-gray-900 rounded-xl shadow-2xl w-full max-w-md border border-gray-200 dark:border-gray-800 p-12" data-id="element-1346">
            <div className="text-center" data-id="element-1347">
              <div className="relative w-20 h-20 mx-auto mb-6" data-id="element-1348">
                <div className="absolute inset-0 border-4 border-gray-200 dark:border-gray-700 rounded-full" data-id="element-1349"></div>
                <div className="absolute inset-0 border-4 border-black dark:border-white border-t-transparent rounded-full animate-spin" data-id="element-1350"></div>
              </div>
              <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-2" data-id="element-1351">
                Procesando tu pedido...
              </h3>
              <p className="text-gray-500 dark:text-gray-400 text-sm" data-id="element-1352">
                Estamos confirmando tu orden
              </p>
            </div>
          </div>
        </div>}

      {/* Confirmation Modal */}
      {showConfirmation && confirmedOrder && <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4 animate-fadeIn" data-id="element-1353">
          <div className="bg-white dark:bg-gray-900 rounded-xl shadow-2xl w-full max-w-lg border border-gray-200 dark:border-gray-800 overflow-hidden animate-slideUp" data-id="element-1354">
            {/* Success Header */}
            <div className="bg-gradient-to-br from-green-500 to-emerald-600 p-8 text-center" data-id="element-1355">
              <div className="w-20 h-20 bg-white rounded-full flex items-center justify-center mx-auto mb-4 shadow-lg animate-scaleIn" data-id="element-1356">
                <CheckCircle className="w-12 h-12 text-green-600" strokeWidth={2.5} data-id="element-1357" />
              </div>
              <h2 className="text-2xl font-bold text-white mb-2" data-id="element-1358">
                ¡Pedido Confirmado!
              </h2>
              <p className="text-green-50" data-id="element-1359">
                Tu pedido ha sido recibido exitosamente
              </p>
            </div>

            {/* Order Details */}
            <div className="p-6 space-y-6 max-h-[60vh] overflow-y-auto" data-id="element-1360">
              {/* Order Info */}
              <div className="space-y-3" data-id="element-1361">
                <div className="flex items-center gap-3 text-sm" data-id="element-1362">
                  <div className="w-10 h-10 bg-blue-100 dark:bg-blue-900/20 rounded-lg flex items-center justify-center" data-id="element-1363">
                    <Package className="w-5 h-5 text-blue-600 dark:text-blue-400" data-id="element-1364" />
                  </div>
                  <div className="flex-1" data-id="element-1365">
                    <p className="text-gray-500 dark:text-gray-400 text-xs" data-id="element-1366">
                      Pedido
                    </p>
                    <p className="font-semibold text-gray-900 dark:text-white" data-id="element-1367">
                      {confirmedOrder.quantity}{' '}
                      {confirmedOrder.quantity === 1 ? 'plato' : 'platos'}
                    </p>
                  </div>
                </div>

                <div className="flex items-center gap-3 text-sm" data-id="element-1368">
                  <div className="w-10 h-10 bg-purple-100 dark:bg-purple-900/20 rounded-lg flex items-center justify-center" data-id="element-1369">
                    <MapPin className="w-5 h-5 text-purple-600 dark:text-purple-400" data-id="element-1370" />
                  </div>
                  <div className="flex-1" data-id="element-1371">
                    <p className="text-gray-500 dark:text-gray-400 text-xs" data-id="element-1372">
                      Entrega
                    </p>
                    <p className="font-semibold text-gray-900 dark:text-white" data-id="element-1373">
                      {confirmedOrder.branch}
                    </p>
                  </div>
                </div>

                <div className="flex items-center gap-3 text-sm" data-id="element-1374">
                  <div className="w-10 h-10 bg-orange-100 dark:bg-orange-900/20 rounded-lg flex items-center justify-center" data-id="element-1375">
                    <Calendar className="w-5 h-5 text-orange-600 dark:text-orange-400" data-id="element-1376" />
                  </div>
                  <div className="flex-1" data-id="element-1377">
                    <p className="text-gray-500 dark:text-gray-400 text-xs" data-id="element-1378">
                      Fecha estimada
                    </p>
                    <p className="font-semibold text-gray-900 dark:text-white" data-id="element-1379">
                      Hoy, 12:00 PM - 2:00 PM
                    </p>
                  </div>
                </div>
              </div>

              {/* Items Summary */}
              <div className="border-t border-gray-200 dark:border-gray-800 pt-4" data-id="element-1380">
                <h3 className="font-semibold text-gray-900 dark:text-white mb-3" data-id="element-1381">
                  Resumen del Pedido
                </h3>
                <div className="space-y-2" data-id="element-1382">
                  {confirmedOrder.items.map(({
                item,
                quantity
              }: any) => <div key={item.id} className="flex justify-between text-sm" data-id="element-1383">
                      <span className="text-gray-600 dark:text-gray-300" data-id="element-1384">
                        {quantity}x {item.name}
                      </span>
                      <span className="font-medium text-gray-900 dark:text-white" data-id="element-1385">
                        RD$ {(item.price * quantity).toFixed(2)}
                      </span>
                    </div>)}
                </div>
              </div>

              {/* Price Breakdown */}
              <div className="border-t border-gray-200 dark:border-gray-800 pt-4 space-y-2" data-id="element-1386">
                <div className="flex justify-between text-sm" data-id="element-1387">
                  <span className="text-gray-500 dark:text-gray-400" data-id="element-1388">
                    Subtotal
                  </span>
                  <span className="text-gray-900 dark:text-white" data-id="element-1389">
                    RD$ {confirmedOrder.subtotal}
                  </span>
                </div>
                {parseFloat(confirmedOrder.tax) > 0 && <div className="flex justify-between text-sm" data-id="element-1390">
                    <span className="text-gray-500 dark:text-gray-400" data-id="element-1391">
                      ITBIS (18%)
                    </span>
                    <span className="text-gray-900 dark:text-white" data-id="element-1392">
                      RD$ {confirmedOrder.tax}
                    </span>
                  </div>}
                {parseFloat(confirmedOrder.subsidyAmount) > 0 && <div className="flex justify-between text-sm" data-id="element-1393">
                    <span className="text-green-600 dark:text-green-400 flex items-center gap-1" data-id="element-1394">
                      <Tag size={14} data-id="element-1395" />
                      Subsidio
                    </span>
                    <span className="text-green-600 dark:text-green-400 font-medium" data-id="element-1396">
                      - RD$ {confirmedOrder.subsidyAmount}
                    </span>
                  </div>}
                <div className="flex justify-between text-lg font-bold pt-2 border-t border-gray-200 dark:border-gray-800" data-id="element-1397">
                  <span className="text-gray-900 dark:text-white" data-id="element-1398">
                    Total Pagado
                  </span>
                  <span className="text-gray-900 dark:text-white" data-id="element-1399">
                    RD$ {confirmedOrder.total}
                  </span>
                </div>
              </div>

              {/* Success Message */}
              <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4" data-id="element-1400">
                <p className="text-sm text-blue-900 dark:text-blue-100 text-center" data-id="element-1401">
                  <strong data-id="element-1402">📧 Confirmación enviada</strong>
                  <br data-id="element-1403" />
                  Recibirás una notificación cuando tu pedido esté listo
                </p>
              </div>
            </div>

            {/* Actions */}
            <div className="p-6 border-t border-gray-200 dark:border-gray-800 flex gap-3" data-id="element-1404">
              <Button variant="outline" onClick={() => setShowConfirmation(false)} fullWidth data-id="element-1405">
                Cerrar
              </Button>
              <Button onClick={() => {
            setShowConfirmation(false);
            navigate('/employee/my-orders');
          }} fullWidth data-id="element-1406">
                Ver Mis Pedidos
              </Button>
            </div>
          </div>
        </div>}
    </div>;
}